import * as React from 'react'

import { VehicleModel } from '@/app/models'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { VehicleGroupModel } from '@/app/models/vehicle/vehicleGroupModel'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import useVehicleService from '../services/VehicleService'
import { CountryModel } from '@/app/models/timesheet/countryModel'

interface VehicleFormData extends Partial<VehicleModel> {
  groups?: number[]
}

interface VehicleFormContextType {
  data: VehicleFormData | null
  setData: (data: VehicleFormData) => void
  isActive: boolean
  setIsActive: (active: boolean) => void
  isEditState: boolean
  groups: VehicleGroupModel[]
  selectedGroups: VehicleGroupModel[]
  countriesOptions: Array<{ label: string; value: string }>
  countries: CountryModel[]
  refreshGroups: () => Promise<void>
  companyIdState: string
  setCompanyIdState: (id: string) => void
  isReadyToSubmit: boolean
  setIsReadyToSubmit: (ready: boolean) => void
}

const VehicleFormContext = React.createContext<VehicleFormContextType | null>(
  null,
)

export const VehicleFormProvider = ({
  children,
  companyId,
  vehicleId,
  isEdit,
}: {
  children: React.ReactNode
  companyId: string
  vehicleId?: string | null
  isEdit?: boolean
}) => {
  const { getVehicleGroups, getVehicle } = useVehicleService()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const [companyIdState, setCompanyIdState] = React.useState<string>(companyId)
  const [isEditState] = React.useState<boolean>(!!isEdit)
  const [isActive, setIsActive] = React.useState<boolean>(true)
  const [data, setData] = React.useState<VehicleFormData | null>(null)
  const [isReadyToSubmit, setIsReadyToSubmit] = React.useState(false)
  const [groups, setGroups] = React.useState<VehicleGroupModel[]>([])

  const { data: countries } = useQueryCountries({
    companyId: companyId,
  })

  const countriesOptions = React.useMemo(() => {
    return Array.isArray(countries)
      ? countries.map((c) => ({
          label: c.name,
          value: c.id.toString(),
        }))
      : []
  }, [countries])

  const fetchVehicle = async () => {
    const stopLoading = startLoading()
    try {
      const vehicleData = await getVehicle({
        companyId,
        vehicleId,
      })
      setIsActive(vehicleData?.isActive ?? true)
      setData((prev) => ({ ...prev, ...vehicleData }))
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }

  const fetchGroups = async () => {
    const stopLoading = startLoading()
    try {
      const res = await getVehicleGroups({
        companyId,
      })
      setGroups(res)
    } catch (err) {
      console.error('Error fetching vehicle groups:', err)
    } finally {
      stopLoading()
    }
  }

  const refreshGroups = React.useCallback(async () => {
    await fetchGroups()
  }, [companyId])

  const selectedGroups = React.useMemo(() => {
    if (!data?.groups || !Array.isArray(data.groups)) return []

    return groups.filter((group) => data.groups.includes(group.id))
  }, [groups, data?.groups])

  React.useEffect(() => {
    const registrationNumber = data?.registrationNumber?.trim() || ''
    setIsReadyToSubmit(registrationNumber.length > 0)
  }, [data?.registrationNumber])

  React.useEffect(() => {
    if (isEdit && vehicleId) {
      fetchVehicle()
    }
  }, [isEdit, vehicleId])

  React.useEffect(() => {
    fetchGroups()
  }, [companyId])

  const value = React.useMemo(
    () => ({
      data,
      setData,
      isActive,
      setIsActive,
      isEditState,
      groups,
      selectedGroups,
      countriesOptions,
      countries,
      refreshGroups,
      companyIdState,
      setCompanyIdState,
      isReadyToSubmit,
      setIsReadyToSubmit,
    }),
    [
      data,
      isActive,
      isEditState,
      groups,
      selectedGroups,
      countriesOptions,
      countries,
      refreshGroups,
      companyIdState,
      isReadyToSubmit,
    ],
  )

  return (
    <VehicleFormContext.Provider value={value}>
      {children}
    </VehicleFormContext.Provider>
  )
}

export const useVehicleForm = () => {
  const ctx = React.useContext(VehicleFormContext)
  if (!ctx)
    throw new Error('useVehicleForm must be used within VehicleFormProvider')
  return ctx
}

